import React, { useEffect, useMemo, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { useNavigate } from "react-router-dom";
import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { auth, db, functions, storage } from "../firebase";
import { getCurrentUserCompanyId, getCurrentUserData } from "../utils/userUtils";
import { hasPermission } from "../utils/permissions";
import logo from "../assets/image/logo_web.png";

const DEFAULT_ASSIGNMENT = {
  allUsers: true,
  userIds: [],
  roles: [],
  groups: [],
};

function createBlankQuestion() {
  return {
    id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    type: "multiple_choice",
    question: "",
    options: ["", "", "", ""],
    correctAnswer: "",
    required: true,
  };
}

function toDate(value) {
  if (!value) return null;
  if (value.toDate) return value.toDate();
  if (value.seconds) return new Date(value.seconds * 1000);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(value) {
  const date = toDate(value);
  return date ? date.toLocaleDateString("en-GB") : "Not set";
}

function safeFileName(name = "document") {
  return String(name).replace(/[^a-zA-Z0-9._-]/g, "_");
}

function getUserGroups(userProfile) {
  return Array.isArray(userProfile?.groups)
    ? userProfile.groups
    : userProfile?.group
      ? [userProfile.group]
      : [];
}

function isAssignedToUser(policy, userProfile) {
  const assignment = policy.assignment || DEFAULT_ASSIGNMENT;
  if (assignment.allUsers !== false) return true;

  const uid = userProfile?.uid || auth.currentUser?.uid;
  const role = String(userProfile?.role || "").toLowerCase();
  const groups = getUserGroups(userProfile);

  return (
    (uid && assignment.userIds?.includes(uid)) ||
    (role && assignment.roles?.map((r) => String(r).toLowerCase()).includes(role)) ||
    groups.some((group) => assignment.groups?.includes(group))
  );
}

function needsAcknowledgement(policy, acknowledgements, userProfile) {
  if (!isAssignedToUser(policy, userProfile)) return false;
  const uid = userProfile?.uid || auth.currentUser?.uid;
  return !getCurrentAcknowledgement(policy, acknowledgements, uid);
}

function isWithinDays(date, days) {
  if (!date) return false;
  const now = new Date();
  const limit = new Date(now);
  limit.setDate(now.getDate() + days);
  return date >= now && date <= limit;
}

function getPolicyLifecycleStatus(policy) {
  const expiry = toDate(policy.expiryDate);
  if (expiry && expiry < new Date()) {
    return { key: "expired", label: "Expired", tone: "bg-red-400/15 text-red-100" };
  }
  if (isWithinDays(expiry, 30)) {
    return { key: "expiring", label: "Expiring soon", tone: "bg-amber-400/15 text-amber-100" };
  }
  return { key: "active", label: "Active", tone: "bg-emerald-400/15 text-emerald-100" };
}

function getAcknowledgement(policy, acknowledgements, userId) {
  return acknowledgements.find(
    (ack) => ack.policyId === policy.id && ack.userId === userId && ack.version === (policy.currentVersion || 1)
  );
}

function getCurrentAcknowledgement(policy, acknowledgements, userId) {
  const ack = getAcknowledgement(policy, acknowledgements, userId);
  if (!ack) return null;

  const signedAt = toDate(ack.signedAt || ack.readConfirmedAt);
  const reviewIntervalDays = Number(policy.reviewIntervalDays || 365);
  if (!signedAt || !Number.isFinite(reviewIntervalDays) || reviewIntervalDays <= 0) {
    return ack;
  }

  const reviewDueAt = new Date(signedAt);
  reviewDueAt.setDate(reviewDueAt.getDate() + reviewIntervalDays);
  return reviewDueAt <= new Date() ? null : ack;
}

function getAssignedUsersForPolicy(policy, users = []) {
  const assignment = policy.assignment || DEFAULT_ASSIGNMENT;
  if (assignment.allUsers !== false) return users;

  return users.filter((user) => {
    const role = String(user.role || "").toLowerCase();
    const userGroups = getUserGroups(user);
    return (
      assignment.userIds?.includes(user.id) ||
      assignment.roles?.map((r) => String(r).toLowerCase()).includes(role) ||
      userGroups.some((group) => assignment.groups?.includes(group))
    );
  });
}

function isPolicyNotification(notification) {
  return [
    "policy_signoff_reminder",
    "policy_lifecycle_alert",
    "policy_signoff_escalation",
  ].includes(notification.type);
}

function canSeePolicyNotification(notification, userProfile, canManage) {
  if (!isPolicyNotification(notification)) return false;
  if (notification.status && notification.status !== "pending") return false;
  if (canManage && (notification.targetRole === "admin" || notification.type !== "policy_signoff_reminder")) return true;

  const uid = userProfile?.uid || auth.currentUser?.uid;
  const role = String(userProfile?.role || "").toLowerCase();
  return (
    notification.userId === uid ||
    (notification.targetRole && notification.targetRole === role)
  );
}

function buildCsv(rows) {
  const headers = [
    "Policy",
    "Version",
    "Version Date",
    "User",
    "Email",
    "Signed At",
    "Assessment Required",
    "Pass Percentage",
    "Assessment Passed",
    "Signature Captured",
    "IP",
    "Device",
  ];

  const escape = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
  return [
    headers.join(","),
    ...rows.map((row) => [
      row.policyTitle,
      row.version,
      row.versionDate,
      row.userName,
      row.userEmail,
      row.signedAt,
      row.assessmentRequired ? "Yes" : "No",
      row.passPercentage,
      row.assessmentPassed,
      row.hasSignature ? "Yes" : "No",
      row.ipAddress || "Not captured",
      row.device || "Unknown",
    ].map(escape).join(",")),
  ].join("\n");
}

function normaliseText(value) {
  return String(value ?? "").trim().toLowerCase();
}

function gradeAssessment(policy, answers = {}) {
  const questions = policy?.assessment?.questions || [];
  if (!policy?.assessment?.enabled || questions.length === 0) {
    return { required: !!policy?.assessment?.enabled, passed: true, score: null, answers, total: 0, correct: 0 };
  }

  const autoScored = questions.filter((question) => question.type !== "short_answer");
  const manualReviewComplete = questions.filter(
    (question) => question.type === "short_answer" && String(answers[question.id] ?? "").trim()
  );
  const requiredIncomplete = questions.some((question) => question.required !== false && !String(answers[question.id] ?? "").trim());
  const correct = autoScored.filter((question) => normaliseText(answers[question.id]) === normaliseText(question.correctAnswer)).length + manualReviewComplete.length;
  const score = questions.length ? Math.round((correct / questions.length) * 100) : (requiredIncomplete ? 0 : 100);
  const passThreshold = Number(policy?.assessment?.passThreshold || 80);

  return {
    required: true,
    passed: !requiredIncomplete && score >= passThreshold,
    score,
    answers,
    total: questions.length,
    correct,
    passThreshold,
  };
}

export default function PoliciesProcedures() {
  const navigate = useNavigate();
  const [companyId, setCompanyId] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [acknowledgements, setAcknowledgements] = useState([]);
  const [policyNotifications, setPolicyNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [versionTarget, setVersionTarget] = useState(null);
  const [assessmentTarget, setAssessmentTarget] = useState(null);
  const [assessmentDraft, setAssessmentDraft] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [automationBusy, setAutomationBusy] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiSummarising, setAiSummarising] = useState(false);
  const signatureRef = useRef(null);
  const expiryDateRef = useRef(null);

  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "General",
    tags: "",
    reviewIntervalDays: 365,
    expiryDate: "",
    requireAssessment: false,
    passThreshold: 80,
    questionCount: 5,
    requireAssessmentBeforeSignoff: false,
    assignment: DEFAULT_ASSIGNMENT,
    linkedChecks: "",
  });
  const [file, setFile] = useState(null);
  const [newVersionFile, setNewVersionFile] = useState(null);

  const canManage = hasPermission(userProfile, "manage_policies");
  const assignedPolicies = useMemo(
    () => policies.filter((policy) => isAssignedToUser(policy, userProfile)),
    [policies, userProfile]
  );
  const pendingPolicies = useMemo(
    () => assignedPolicies.filter((policy) => needsAcknowledgement(policy, acknowledgements, userProfile)),
    [assignedPolicies, acknowledgements, userProfile]
  );
  const complianceRows = useMemo(() => {
    return policies.flatMap((policy) =>
      getAssignedUsersForPolicy(policy, users).map((user) => {
        const ack = getCurrentAcknowledgement(policy, acknowledgements, user.id);
        const lifecycle = getPolicyLifecycleStatus(policy);
        return {
          id: `${policy.id}_${user.id}`,
          policy,
          user,
          ack,
          lifecycle,
          status: ack ? "signed" : "missing",
        };
      })
    );
  }, [policies, users, acknowledgements]);
  const displayedPolicies = useMemo(() => {
    const q = search.trim().toLowerCase();
    return assignedPolicies.filter((policy) => {
      const lifecycle = getPolicyLifecycleStatus(policy);
      const signed = !needsAcknowledgement(policy, acknowledgements, userProfile);
      const matchesSearch = !q || [
        policy.title,
        policy.description,
        policy.category,
        ...(policy.tags || []),
      ].some((value) => String(value || "").toLowerCase().includes(q));
      const matchesStatus = statusFilter === "all"
        || (statusFilter === "pending" && !signed)
        || (statusFilter === "signed" && signed)
        || lifecycle.key === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [assignedPolicies, search, statusFilter, acknowledgements, userProfile]);
  const riskRows = complianceRows.filter((row) => row.status === "missing" || row.lifecycle.key !== "active");
  const visiblePolicyNotifications = useMemo(
    () => policyNotifications.filter((notification) => canSeePolicyNotification(notification, userProfile, canManage)),
    [policyNotifications, userProfile, canManage]
  );

  useEffect(() => {
    let unsubscribePolicies = null;
    let unsubscribeNotifications = null;
    let mounted = true;

    async function load() {
      const [cid, profile] = await Promise.all([
        getCurrentUserCompanyId(),
        getCurrentUserData(),
      ]);
      if (!mounted) return;
      setCompanyId(cid);
      setUserProfile({ ...profile, uid: auth.currentUser?.uid });

      if (!cid) {
        setLoading(false);
        return;
      }

      const [usersSnap, groupsSnap] = await Promise.all([
        getDocs(query(collection(db, "users"))),
        getDocs(collection(db, "companies", cid, "groups")),
      ]);

      if (!mounted) return;
      setUsers(
        usersSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((u) => u.companyId === cid)
      );
      setGroups(groupsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

      unsubscribePolicies = onSnapshot(
        query(collection(db, "companies", cid, "policies"), orderBy("updatedAt", "desc")),
        async (snapshot) => {
          const policyRows = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
          setPolicies(policyRows);

          const ackRows = [];
          for (const policy of policyRows) {
            const ackSnap = await getDocs(collection(db, "companies", cid, "policies", policy.id, "acknowledgements"));
            ackSnap.forEach((ackDoc) => ackRows.push({ id: ackDoc.id, policyId: policy.id, ...ackDoc.data() }));
          }
          setAcknowledgements(ackRows);
          setLoading(false);
        },
        (error) => {
          console.error("Failed to load policies:", error);
          setLoading(false);
        }
      );

      unsubscribeNotifications = onSnapshot(
        query(collection(db, "companies", cid, "notifications"), orderBy("createdAt", "desc")),
        (snapshot) => {
          if (!mounted) return;
          setPolicyNotifications(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })).filter(isPolicyNotification));
        },
        (error) => {
          console.error("Failed to load policy notifications:", error);
        }
      );
    }

    load();
    return () => {
      mounted = false;
      if (unsubscribePolicies) unsubscribePolicies();
      if (unsubscribeNotifications) unsubscribeNotifications();
    };
  }, []);

  const resetForm = () => {
    setForm({
      title: "",
      description: "",
      category: "General",
      tags: "",
      reviewIntervalDays: 365,
      expiryDate: "",
      requireAssessment: false,
      passThreshold: 80,
      questionCount: 5,
      requireAssessmentBeforeSignoff: false,
      assignment: DEFAULT_ASSIGNMENT,
      linkedChecks: "",
    });
    setFile(null);
  };

  const handleCreatePolicy = async (event) => {
    event.preventDefault();
    if (!companyId || !file || !form.title.trim()) {
      alert("Please add a title and choose a document file.");
      return;
    }

    setSaving(true);
    try {
      const policyRef = doc(collection(db, "companies", companyId, "policies"));
      const version = 1;
      const path = `companies/${companyId}/policies/${policyRef.id}/v${version}_${Date.now()}_${safeFileName(file.name)}`;
      const fileRef = ref(storage, path);
      await uploadBytes(fileRef, file, { contentType: file.type || "application/octet-stream" });
      const downloadURL = await getDownloadURL(fileRef);

      const now = new Date().toISOString();
      const versionRecord = {
        version,
        fileName: file.name,
        fileType: file.type || "application/octet-stream",
        size: file.size,
        storagePath: path,
        downloadURL,
        uploadedAt: now,
        uploadedBy: auth.currentUser?.uid || null,
        archived: false,
      };

      await setDoc(policyRef, {
        title: form.title.trim(),
        description: form.description.trim(),
        category: form.category.trim() || "General",
        tags: form.tags.split(",").map((tag) => tag.trim()).filter(Boolean),
        status: "published",
        currentVersion: version,
        currentFile: versionRecord,
        versions: [versionRecord],
        assignment: form.assignment,
        reviewIntervalDays: Number(form.reviewIntervalDays) || 365,
        expiryDate: form.expiryDate || null,
        assessment: {
          enabled: !!form.requireAssessment,
          passThreshold: Number(form.passThreshold) || 80,
          questionCount: Number(form.questionCount) || 5,
          requireBeforeSignoff: !!form.requireAssessmentBeforeSignoff,
          questions: [],
          aiGenerationStatus: "not_started",
        },
        linkedChecks: form.linkedChecks.split(",").map((item) => item.trim()).filter(Boolean),
        createdAt: now,
        createdBy: auth.currentUser?.uid || null,
        updatedAt: now,
        updatedBy: auth.currentUser?.uid || null,
      });

      await addDoc(collection(db, "companies", companyId, "policies", policyRef.id, "activity"), {
        type: "created",
        actorId: auth.currentUser?.uid || null,
        actorEmail: auth.currentUser?.email || null,
        createdAt: now,
        details: { version, fileName: file.name },
      });

      resetForm();
    } catch (error) {
      console.error("Create policy failed:", error);
      alert(error.message || "Could not create policy.");
    } finally {
      setSaving(false);
    }
  };

  const handleUploadNewVersion = async () => {
    if (!companyId || !versionTarget || !newVersionFile) return;

    setSaving(true);
    try {
      const nextVersion = Number(versionTarget.currentVersion || 1) + 1;
      const path = `companies/${companyId}/policies/${versionTarget.id}/v${nextVersion}_${Date.now()}_${safeFileName(newVersionFile.name)}`;
      const fileRef = ref(storage, path);
      await uploadBytes(fileRef, newVersionFile, { contentType: newVersionFile.type || "application/octet-stream" });
      const downloadURL = await getDownloadURL(fileRef);
      const now = new Date().toISOString();
      const versionRecord = {
        version: nextVersion,
        fileName: newVersionFile.name,
        fileType: newVersionFile.type || "application/octet-stream",
        size: newVersionFile.size,
        storagePath: path,
        downloadURL,
        uploadedAt: now,
        uploadedBy: auth.currentUser?.uid || null,
        archived: false,
      };

      await updateDoc(doc(db, "companies", companyId, "policies", versionTarget.id), {
        currentVersion: nextVersion,
        currentFile: versionRecord,
        versions: arrayUnion(versionRecord),
        updatedAt: now,
        updatedBy: auth.currentUser?.uid || null,
      });

      await addDoc(collection(db, "companies", companyId, "policies", versionTarget.id, "activity"), {
        type: "new_version",
        actorId: auth.currentUser?.uid || null,
        actorEmail: auth.currentUser?.email || null,
        createdAt: now,
        details: { version: nextVersion, fileName: newVersionFile.name },
      });

      setVersionTarget(null);
      setNewVersionFile(null);
    } catch (error) {
      console.error("Upload version failed:", error);
      alert(error.message || "Could not upload new version.");
    } finally {
      setSaving(false);
    }
  };

  const openAssessmentEditor = (policy) => {
    setAssessmentTarget(policy);
    setAssessmentDraft({
      enabled: !!policy.assessment?.enabled,
      passThreshold: Number(policy.assessment?.passThreshold || 80),
      questionCount: Number(policy.assessment?.questionCount || 5),
      requireBeforeSignoff: !!policy.assessment?.requireBeforeSignoff,
      questions: Array.isArray(policy.assessment?.questions) && policy.assessment.questions.length > 0
        ? policy.assessment.questions
        : [createBlankQuestion()],
      aiGenerationStatus: policy.assessment?.aiGenerationStatus || "not_started",
      aiSourceText: "",
      aiDocumentSummary: policy.assessment?.aiDocumentSummary || "",
      aiSummaryGeneratedAt: policy.assessment?.aiSummaryGeneratedAt || null,
      aiSummaryModel: policy.assessment?.aiSummaryModel || null,
      aiSummaryUsedPolicyFile: !!policy.assessment?.aiSummaryUsedPolicyFile,
    });
  };

  const updateAssessmentQuestion = (questionId, patch) => {
    setAssessmentDraft((current) => ({
      ...current,
      questions: (current?.questions || []).map((question) =>
        question.id === questionId ? { ...question, ...patch } : question
      ),
    }));
  };

  const updateQuestionOption = (questionId, optionIndex, value) => {
    setAssessmentDraft((current) => ({
      ...current,
      questions: (current?.questions || []).map((question) => {
        if (question.id !== questionId) return question;
        const options = [...(question.options || [])];
        options[optionIndex] = value;
        return { ...question, options };
      }),
    }));
  };

  const saveAssessment = async () => {
    if (!companyId || !assessmentTarget || !assessmentDraft) return;

    const questions = (assessmentDraft.questions || [])
      .map((question) => ({
        ...question,
        question: question.question.trim(),
        options: question.type === "multiple_choice"
          ? (question.options || []).map((option) => option.trim()).filter(Boolean)
          : question.type === "true_false"
            ? ["true", "false"]
            : [],
        correctAnswer: question.type === "short_answer" ? "" : String(question.correctAnswer || "").trim(),
      }))
      .filter((question) => question.question);

    const invalid = questions.some((question) =>
      question.type !== "short_answer" && !question.correctAnswer
    );
    if (invalid) {
      alert("Please set the correct answer for every scored question.");
      return;
    }

    setSaving(true);
    try {
      const { aiSourceText, ...assessmentData } = assessmentDraft;
      await updateDoc(doc(db, "companies", companyId, "policies", assessmentTarget.id), {
        assessment: {
          ...assessmentData,
          questions,
          questionCount: questions.length,
        },
        updatedAt: new Date().toISOString(),
        updatedBy: auth.currentUser?.uid || null,
      });
      await addDoc(collection(db, "companies", companyId, "policies", assessmentTarget.id, "activity"), {
        type: "assessment_updated",
        actorId: auth.currentUser?.uid || null,
        actorEmail: auth.currentUser?.email || null,
        createdAt: new Date().toISOString(),
        details: { questionCount: questions.length },
      });
      setAssessmentTarget(null);
      setAssessmentDraft(null);
    } catch (error) {
      console.error("Save assessment failed:", error);
      alert(error.message || "Could not save assessment.");
    } finally {
      setSaving(false);
    }
  };

  const generateAssessmentWithAI = async () => {
    if (!companyId || !assessmentTarget || !assessmentDraft) return;
    setAiGenerating(true);
    try {
      const generateQuestions = httpsCallable(functions, "generatePolicyAssessmentQuestions");
      const result = await generateQuestions({
        companyId,
        policyId: assessmentTarget.id,
        questionCount: Number(assessmentDraft.questionCount || 5),
        sourceText: assessmentDraft.aiDocumentSummary || assessmentDraft.aiSourceText || "",
      });
      const questions = (result.data?.questions || []).map((question) => ({
        ...question,
        id: question.id || `ai_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        required: question.required !== false,
      }));

      if (questions.length === 0) {
        throw new Error("No questions were generated.");
      }

      setAssessmentDraft({
        ...assessmentDraft,
        enabled: true,
        questions,
        questionCount: questions.length,
        aiGenerationStatus: "generated",
        aiGeneratedAt: new Date().toISOString(),
        aiModel: result.data?.model || null,
        aiUsedPolicyFile: !!result.data?.usedPolicyFile,
        aiUsedStoredSummary: !!result.data?.usedStoredSummary,
      });
      alert(`Generated ${questions.length} AI questions${result.data?.usedStoredSummary ? " from the saved policy summary" : result.data?.usedPolicyFile ? " from the uploaded PDF" : ""}. Please review and save before publishing.`);
    } catch (error) {
      console.error("AI question generation failed:", error);
      alert(error.message || "Could not generate AI questions.");
    } finally {
      setAiGenerating(false);
    }
  };

  const generatePolicySummary = async () => {
    if (!companyId || !assessmentTarget || !assessmentDraft) return;
    setAiSummarising(true);
    try {
      const generateSummary = httpsCallable(functions, "generatePolicySummary");
      const result = await generateSummary({
        companyId,
        policyId: assessmentTarget.id,
        sourceText: assessmentDraft.aiSourceText || "",
      });
      const summary = result.data?.summary || "";
      if (!summary) {
        throw new Error("No summary was generated.");
      }

      setAssessmentDraft({
        ...assessmentDraft,
        aiDocumentSummary: summary,
        aiSummaryGeneratedAt: new Date().toISOString(),
        aiSummaryModel: result.data?.model || null,
        aiSummaryUsedPolicyFile: !!result.data?.usedPolicyFile,
      });
      alert(`Policy summary generated${result.data?.usedPolicyFile ? " from the uploaded PDF" : ""}. You can review it, edit it, then generate questions from the summary.`);
    } catch (error) {
      console.error("AI policy summary failed:", error);
      alert(error.message || "Could not generate policy summary.");
    } finally {
      setAiSummarising(false);
    }
  };

  const createReminder = async (row) => {
    if (!companyId || !row?.user?.id || !row?.policy?.id) return;
    try {
      const now = new Date().toISOString();
      await addDoc(collection(db, "companies", companyId, "notifications"), {
        type: "policy_signoff_reminder",
        title: `Policy sign-off required: ${row.policy.title}`,
        message: `${row.user.fullName || row.user.email || "A user"} needs to acknowledge ${row.policy.title} v${row.policy.currentVersion || 1}.`,
        userId: row.user.id,
        policyId: row.policy.id,
        policyTitle: row.policy.title,
        version: row.policy.currentVersion || 1,
        status: "pending",
        priority: row.lifecycle.key === "expired" ? "high" : "normal",
        createdAt: now,
        createdBy: auth.currentUser?.uid || null,
      });
      await addDoc(collection(db, "companies", companyId, "policies", row.policy.id, "activity"), {
        type: "reminder_created",
        actorId: auth.currentUser?.uid || null,
        actorEmail: auth.currentUser?.email || null,
        createdAt: now,
        details: { userId: row.user.id, version: row.policy.currentVersion || 1 },
      });
      alert("Reminder queued.");
    } catch (error) {
      console.error("Reminder failed:", error);
      alert(error.message || "Could not create reminder.");
    }
  };

  const runComplianceAutomation = async () => {
    if (!companyId) return;
    setAutomationBusy(true);
    try {
      const scan = httpsCallable(functions, "runPolicyComplianceScan");
      const result = await scan({ companyId });
      const data = result.data || {};
      alert(`Compliance scan complete. ${data.createdReminders || 0} reminders created, ${data.createdLifecycleAlerts || 0} lifecycle alerts created, ${data.createdEscalations || 0} overdue items escalated, ${data.skippedExisting || 0} existing alerts skipped.`);
    } catch (error) {
      console.error("Policy compliance scan failed:", error);
      alert(error.message || "Could not run compliance automation.");
    } finally {
      setAutomationBusy(false);
    }
  };

  const resolvePolicyNotification = async (notification) => {
    if (!companyId || !notification?.id) return;
    try {
      await updateDoc(doc(db, "companies", companyId, "notifications", notification.id), {
        status: "resolved",
        resolvedAt: new Date().toISOString(),
        resolvedBy: auth.currentUser?.uid || null,
      });
    } catch (error) {
      console.error("Resolve policy notification failed:", error);
      alert(error.message || "Could not resolve this alert.");
    }
  };

  const resolveMatchingPolicyReminders = async (policyId, version) => {
    const uid = auth.currentUser?.uid;
    if (!companyId || !uid) return;

    const matches = policyNotifications.filter(
      (notification) =>
        notification.type === "policy_signoff_reminder" &&
        notification.status === "pending" &&
        notification.policyId === policyId &&
        notification.userId === uid &&
        Number(notification.version || 1) === Number(version || 1)
    );

    await Promise.all(matches.map((notification) => resolvePolicyNotification(notification)));
  };

  const handleSignPolicy = async () => {
    if (!companyId || !selectedPolicy) return;
    const assessmentResult = gradeAssessment(selectedPolicy, quizAnswers);
    if (selectedPolicy.assessment?.requireBeforeSignoff && !assessmentResult.passed) {
      setQuizResult(assessmentResult);
      alert(`Assessment not passed. Score: ${assessmentResult.score ?? 0}%. Required: ${assessmentResult.passThreshold || selectedPolicy.assessment?.passThreshold || 80}%.`);
      return;
    }
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      alert("Please provide a signature before signing off.");
      return;
    }

    setSaving(true);
    try {
      const uid = auth.currentUser?.uid;
      const version = selectedPolicy.currentVersion || 1;
      const now = new Date().toISOString();
      const ackId = `${uid}_v${version}`;

      await setDoc(doc(db, "companies", companyId, "policies", selectedPolicy.id, "acknowledgements", ackId), {
        policyId: selectedPolicy.id,
        policyTitle: selectedPolicy.title,
        version,
        userId: uid,
        userEmail: auth.currentUser?.email || userProfile?.email || null,
        userName: userProfile?.fullName || userProfile?.displayName || auth.currentUser?.email || uid,
        signedAt: now,
        readConfirmedAt: now,
        signature: signatureRef.current.toDataURL(),
        userAgent: navigator.userAgent,
        device: navigator.platform || "web",
        ipAddress: null,
        assessment: {
          ...assessmentResult,
        },
      });

      await addDoc(collection(db, "companies", companyId, "policies", selectedPolicy.id, "activity"), {
        type: "acknowledged",
        actorId: uid,
        actorEmail: auth.currentUser?.email || null,
        createdAt: now,
        details: { version },
      });

      await resolveMatchingPolicyReminders(selectedPolicy.id, version);

      setSelectedPolicy(null);
      setQuizAnswers({});
      setQuizResult(null);
    } catch (error) {
      console.error("Sign policy failed:", error);
      alert(error.message || "Could not sign policy.");
    } finally {
      setSaving(false);
    }
  };

  const exportTrainingRecords = () => {
    const rows = acknowledgements.map((ack) => {
      const policy = policies.find((p) => p.id === ack.policyId);
      const versionRecord = (policy?.versions || []).find((version) => Number(version.version) === Number(ack.version));
      const assessmentRequired = !!policy?.assessment?.enabled;
      const score = ack.assessment?.score;
      const passed = ack.assessment?.passed;

      return {
        policyTitle: ack.policyTitle || policy?.title || ack.policyId,
        version: ack.version,
        versionDate: formatDate(versionRecord?.uploadedAt || policy?.currentFile?.uploadedAt || policy?.createdAt),
        userName: ack.userName,
        userEmail: ack.userEmail,
        signedAt: formatDate(ack.signedAt),
        assessmentRequired,
        passPercentage: assessmentRequired && score != null ? `${score}%` : "N/A",
        assessmentPassed: assessmentRequired ? (passed ? "Yes" : "No") : "N/A",
        hasSignature: !!ack.signature,
        ipAddress: ack.ipAddress,
        device: ack.device || ack.userAgent,
      };
    });
    const blob = new Blob([buildCsv(rows)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `policy-training-records-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-950 text-white p-8">Loading policies...</div>;
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,#0f766e33,transparent_35%),linear-gradient(135deg,#020617,#0f172a_55%,#111827)] text-white px-6 py-8">
      <header className="sticky top-0 z-40 -mx-6 -mt-8 mb-8 px-6 py-4 bg-slate-950/80 backdrop-blur-xl border-b border-white/10 shadow-2xl">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center">
              <img src={logo} alt="BuildMyChecks" className="w-9 h-9 object-contain" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-white/60">BuildMyChecks</p>
              <h2 className="text-xl font-extrabold text-white">Policies & Procedures</h2>
            </div>
          </div>
          <nav className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => navigate("/home")}
              className="px-3 py-2 rounded-full border border-white/15 bg-white/5 text-white/85 hover:bg-white/15 transition"
            >
              Home
            </button>
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="px-3 py-2 rounded-full border border-white/15 bg-white/5 text-white/85 hover:bg-white/15 transition"
            >
              Dashboard
            </button>
            <button
              type="button"
              onClick={() => navigate("/knowledge-hub")}
              className="px-3 py-2 rounded-full bg-teal-400 text-slate-950 font-bold hover:bg-teal-300 transition"
            >
              Knowledge Hub
            </button>
          </nav>
        </div>
      </header>
      <div className="max-w-7xl mx-auto space-y-8">
        <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div>
            <p className="uppercase tracking-[0.28em] text-teal-300 text-xs">BuildMyChecks Compliance Hub</p>
            <h1 className="text-4xl font-black mt-2">Policies & Procedures</h1>
            <p className="text-slate-300 mt-3 max-w-3xl">
              Centralise controlled documents, track versioned staff sign-off, and maintain training evidence for audits.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-3 min-w-[320px]">
            <div className="rounded-2xl bg-white/10 border border-white/10 p-4">
              <div className="text-3xl font-black">{policies.length}</div>
              <div className="text-xs text-slate-300">Documents</div>
            </div>
            <div className="rounded-2xl bg-amber-400/15 border border-amber-300/20 p-4">
              <div className="text-3xl font-black">{pendingPolicies.length}</div>
              <div className="text-xs text-amber-100">Need sign-off</div>
            </div>
            <div className="rounded-2xl bg-emerald-400/15 border border-emerald-300/20 p-4">
              <div className="text-3xl font-black">{acknowledgements.length}</div>
              <div className="text-xs text-emerald-100">Records</div>
            </div>
          </div>
        </header>

        <section className="rounded-3xl bg-white/10 border border-white/10 p-5 grid lg:grid-cols-4 gap-4">
          <div className="lg:col-span-2">
            <label className="text-xs uppercase tracking-[0.2em] text-slate-400">Search knowledge hub</label>
            <input
              className="mt-2 w-full rounded-2xl bg-slate-950/70 border border-white/10 px-4 py-3"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search title, category, description or tags"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-slate-400">Status</label>
            <select
              className="mt-2 w-full rounded-2xl bg-slate-950/70 border border-white/10 px-4 py-3"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="all">All policies</option>
              <option value="pending">Needs my sign-off</option>
              <option value="signed">Signed by me</option>
              <option value="active">Active lifecycle</option>
              <option value="expiring">Expiring soon</option>
              <option value="expired">Expired</option>
            </select>
          </div>
          <div className="rounded-2xl bg-red-400/10 border border-red-300/20 p-4">
            <div className="text-3xl font-black">{riskRows.length}</div>
            <div className="text-xs text-red-100">Compliance risks</div>
          </div>
          {canManage && (
            <button
              type="button"
              onClick={runComplianceAutomation}
              disabled={automationBusy}
              className="lg:col-span-4 rounded-2xl bg-teal-400 text-slate-950 font-black px-5 py-3 disabled:opacity-60"
            >
              {automationBusy ? "Running compliance automation..." : "Run compliance automation"}
            </button>
          )}
        </section>

        {visiblePolicyNotifications.length > 0 && (
          <section className="rounded-3xl bg-amber-400/10 border border-amber-300/20 p-5">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <h2 className="text-2xl font-black">Policy alerts</h2>
                <p className="text-sm text-amber-100">Automation reminders, lifecycle alerts, and escalations that need attention.</p>
              </div>
              <span className="px-3 py-1 rounded-full bg-amber-300 text-slate-950 text-sm font-black self-start">
                {visiblePolicyNotifications.length} open
              </span>
            </div>
            <div className="grid lg:grid-cols-3 gap-3 mt-4">
              {visiblePolicyNotifications.slice(0, 6).map((notification) => {
                const policy = policies.find((p) => p.id === notification.policyId);
                return (
                  <div key={notification.id} className="rounded-2xl bg-slate-950/60 border border-amber-300/20 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-amber-200">
                          {notification.type === "policy_signoff_escalation" ? "Escalation" : notification.type === "policy_lifecycle_alert" ? "Lifecycle" : "Reminder"}
                        </div>
                        <div className="font-black mt-1">{notification.title || "Policy alert"}</div>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-[11px] font-bold ${notification.priority === "high" ? "bg-red-400 text-white" : "bg-amber-300 text-slate-950"}`}>
                        {notification.priority || "normal"}
                      </span>
                    </div>
                    <p className="text-sm text-slate-300 mt-2">{notification.message}</p>
                    <div className="flex flex-wrap gap-2 mt-4">
                      {policy && (
                        <button className="px-3 py-2 rounded-xl bg-white/10 border border-white/10 text-sm font-bold" onClick={() => setSelectedPolicy(policy)}>
                          Open policy
                        </button>
                      )}
                      <button className="px-3 py-2 rounded-xl bg-emerald-400 text-slate-950 text-sm font-black" onClick={() => resolvePolicyNotification(notification)}>
                        Mark resolved
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {canManage && (
          <section className="rounded-3xl bg-white/10 border border-white/10 p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-2xl font-bold">Upload controlled document</h2>
                <p className="text-sm text-slate-300">Supports PDF, Word, Excel, PowerPoint, images, and common file formats.</p>
              </div>
              <span className="px-3 py-1 rounded-full bg-teal-400/15 text-teal-100 text-xs border border-teal-300/20">Version 1 created on upload</span>
            </div>

            <form onSubmit={handleCreatePolicy} className="grid lg:grid-cols-4 gap-4">
              <input className="rounded-xl bg-slate-950/70 border border-white/10 px-4 py-3 lg:col-span-2" placeholder="Policy title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <input className="rounded-xl bg-slate-950/70 border border-white/10 px-4 py-3" placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
              <input className="rounded-xl bg-slate-950/70 border border-white/10 px-4 py-3" placeholder="Tags comma separated" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
              <textarea className="rounded-xl bg-slate-950/70 border border-white/10 px-4 py-3 lg:col-span-4" placeholder="Description / compliance purpose" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <label className="rounded-xl bg-slate-950/70 border border-white/10 px-4 py-3">
                <span className="block text-xs text-slate-400 mb-1">Review interval days</span>
                <input className="w-full bg-transparent outline-none" type="number" value={form.reviewIntervalDays} onChange={(e) => setForm({ ...form, reviewIntervalDays: e.target.value })} />
              </label>
              <label className="rounded-xl bg-slate-950/70 border border-white/10 px-4 py-3">
                <span className="block text-xs text-slate-400 mb-1">Expiry date</span>
                <div className="flex items-center gap-3">
                  <input ref={expiryDateRef} className="w-full bg-transparent outline-none text-white [color-scheme:dark]" type="date" value={form.expiryDate} onChange={(e) => setForm({ ...form, expiryDate: e.target.value })} />
                  <button
                    type="button"
                    aria-label="Open expiry date calendar"
                    className="shrink-0 rounded-lg bg-white/15 border border-white/20 px-3 py-2 text-white hover:bg-white/25"
                    onClick={() => {
                      if (expiryDateRef.current?.showPicker) {
                        expiryDateRef.current.showPicker();
                      } else {
                        expiryDateRef.current?.focus();
                      }
                    }}
                  >
                    Calendar
                  </button>
                </div>
              </label>
              <label className="rounded-xl bg-slate-950/70 border border-white/10 px-4 py-3 lg:col-span-2">
                <span className="block text-xs text-slate-400 mb-1">Document file</span>
                <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
              </label>

              <div className="lg:col-span-2 rounded-2xl bg-slate-950/50 border border-white/10 p-4 space-y-3">
                <div className="font-bold">Targeted assignment</div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.assignment.allUsers !== false} onChange={(e) => setForm({ ...form, assignment: { ...form.assignment, allUsers: e.target.checked } })} />
                  Assign to all users
                </label>
                <div className="grid md:grid-cols-3 gap-3">
                  <select multiple className="min-h-28 rounded-xl bg-slate-900 border border-white/10 p-2" disabled={form.assignment.allUsers !== false} value={form.assignment.userIds} onChange={(e) => setForm({ ...form, assignment: { ...form.assignment, userIds: Array.from(e.target.selectedOptions).map((o) => o.value) } })}>
                    {users.map((u) => <option key={u.id} value={u.id}>{u.fullName || u.email || u.id}</option>)}
                  </select>
                  <select multiple className="min-h-28 rounded-xl bg-slate-900 border border-white/10 p-2" disabled={form.assignment.allUsers !== false} value={form.assignment.roles} onChange={(e) => setForm({ ...form, assignment: { ...form.assignment, roles: Array.from(e.target.selectedOptions).map((o) => o.value) } })}>
                    {["admin", "manager", "super-user", "user", "viewer"].map((role) => <option key={role} value={role}>{role}</option>)}
                  </select>
                  <select multiple className="min-h-28 rounded-xl bg-slate-900 border border-white/10 p-2" disabled={form.assignment.allUsers !== false} value={form.assignment.groups} onChange={(e) => setForm({ ...form, assignment: { ...form.assignment, groups: Array.from(e.target.selectedOptions).map((o) => o.value) } })}>
                    {groups.map((group) => <option key={group.id} value={group.id}>{group.name || group.id}</option>)}
                  </select>
                </div>
              </div>

              <div className="lg:col-span-2 rounded-2xl bg-slate-950/50 border border-white/10 p-4 space-y-3">
                <div className="font-bold">Closeout assessment</div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.requireAssessment} onChange={(e) => setForm({ ...form, requireAssessment: e.target.checked })} />
                  Attach quiz to this document
                </label>
                <div className="grid grid-cols-3 gap-3">
                  <input className="rounded-xl bg-slate-900 border border-white/10 p-2" type="number" placeholder="Pass %" value={form.passThreshold} onChange={(e) => setForm({ ...form, passThreshold: e.target.value })} />
                  <input className="rounded-xl bg-slate-900 border border-white/10 p-2" type="number" placeholder="Questions" value={form.questionCount} onChange={(e) => setForm({ ...form, questionCount: e.target.value })} />
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={form.requireAssessmentBeforeSignoff} onChange={(e) => setForm({ ...form, requireAssessmentBeforeSignoff: e.target.checked })} />
                    Required
                  </label>
                </div>
                <div className="rounded-xl bg-indigo-400/10 border border-indigo-300/20 p-3 text-sm text-indigo-100">
                  AI question generation will use these settings once the document analysis backend is connected.
                </div>
              </div>

              <input className="rounded-xl bg-slate-950/70 border border-white/10 px-4 py-3 lg:col-span-3" placeholder="Linked checks/incidents/audit refs comma separated" value={form.linkedChecks} onChange={(e) => setForm({ ...form, linkedChecks: e.target.value })} />
              <button disabled={saving} className="rounded-xl bg-teal-500 hover:bg-teal-400 text-slate-950 font-black px-5 py-3 disabled:opacity-60">
                {saving ? "Saving..." : "Publish policy"}
              </button>
            </form>
          </section>
        )}

        <section className="grid xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold">Knowledge hub</h2>
              {canManage && (
                <button onClick={exportTrainingRecords} className="px-4 py-2 rounded-xl bg-white text-slate-950 font-bold">
                  Export training records
                </button>
              )}
            </div>

            {displayedPolicies.length === 0 ? (
              <div className="rounded-3xl bg-white/10 border border-white/10 p-8 text-slate-300">No policies assigned yet.</div>
            ) : (
              displayedPolicies.map((policy) => {
                const pending = needsAcknowledgement(policy, acknowledgements, userProfile);
                const lifecycle = getPolicyLifecycleStatus(policy);
                return (
                  <article key={policy.id} className="rounded-3xl bg-white/10 border border-white/10 p-5">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                      <div>
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className="px-3 py-1 rounded-full bg-slate-950/60 border border-white/10 text-xs">{policy.category || "General"}</span>
                          <span className="px-3 py-1 rounded-full bg-blue-400/15 text-blue-100 text-xs">v{policy.currentVersion || 1}</span>
                          <span className={`px-3 py-1 rounded-full text-xs ${lifecycle.tone}`}>{lifecycle.label}</span>
                          {pending ? <span className="px-3 py-1 rounded-full bg-amber-400/15 text-amber-100 text-xs">Sign-off required</span> : <span className="px-3 py-1 rounded-full bg-emerald-400/15 text-emerald-100 text-xs">Acknowledged</span>}
                        </div>
                        <h3 className="text-xl font-black">{policy.title}</h3>
                        <p className="text-slate-300 mt-2">{policy.description || "No description provided."}</p>
                        <div className="text-xs text-slate-400 mt-3">
                          Review every {policy.reviewIntervalDays || 365} days • Expires {formatDate(policy.expiryDate)} • Latest file: {policy.currentFile?.fileName || "Unknown"}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {policy.currentFile?.downloadURL && (
                          <a className="px-4 py-2 rounded-xl bg-slate-950 border border-white/10 font-bold" href={policy.currentFile.downloadURL} target="_blank" rel="noreferrer">
                            Open document
                          </a>
                        )}
                        {pending && (
                          <button className="px-4 py-2 rounded-xl bg-emerald-500 text-slate-950 font-black" onClick={() => setSelectedPolicy(policy)}>
                            Read & sign
                          </button>
                        )}
                        {canManage && (
                          <>
                            <button className="px-4 py-2 rounded-xl bg-white/10 border border-white/10 font-bold" onClick={() => openAssessmentEditor(policy)}>
                              Assessment
                            </button>
                            <button className="px-4 py-2 rounded-xl bg-white/10 border border-white/10 font-bold" onClick={() => setVersionTarget(policy)}>
                              New version
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="mt-4 grid md:grid-cols-2 gap-3 text-sm">
                      <div className="rounded-2xl bg-slate-950/40 border border-white/10 p-4">
                        <div className="font-bold mb-2">Version history</div>
                        {(policy.versions || []).slice().reverse().map((version) => (
                          <div key={`${policy.id}_${version.version}`} className="flex justify-between gap-3 border-t border-white/10 py-2">
                            <span>v{version.version} • {version.fileName}</span>
                            <span className="text-slate-400">{formatDate(version.uploadedAt)}</span>
                          </div>
                        ))}
                      </div>
                      <div className="rounded-2xl bg-slate-950/40 border border-white/10 p-4">
                        <div className="font-bold mb-2">Acknowledgements</div>
                        {acknowledgements.filter((ack) => ack.policyId === policy.id && ack.version === policy.currentVersion).length === 0 ? (
                          <div className="text-slate-400">No current-version sign-offs yet.</div>
                        ) : (
                          acknowledgements
                            .filter((ack) => ack.policyId === policy.id && ack.version === policy.currentVersion)
                            .map((ack) => (
                              <div key={ack.id} className="flex justify-between gap-3 border-t border-white/10 py-2">
                                <span>{ack.userName || ack.userEmail || ack.userId}</span>
                                <span className="text-slate-400">{formatDate(ack.signedAt)}</span>
                              </div>
                            ))
                        )}
                      </div>
                    </div>
                  </article>
                );
              })
            )}
          </div>

          <aside className="space-y-4">
            <div className="rounded-3xl bg-white/10 border border-white/10 p-5">
              <h2 className="text-xl font-black">Compliance risks</h2>
              <div className="mt-4 space-y-3">
                {riskRows.length === 0 ? (
                  <div className="text-emerald-200">No outstanding policy sign-offs for you.</div>
                ) : (
                  riskRows.slice(0, 8).map((row) => (
                    <div key={row.id} className="rounded-2xl bg-amber-400/10 border border-amber-300/20 p-3">
                      <div className="font-bold">{row.policy.title}</div>
                      <div className="text-xs text-amber-100 mt-1">
                        {row.user.fullName || row.user.email || row.user.id} • {row.status === "missing" ? "Missing sign-off" : row.lifecycle.label}
                      </div>
                      {canManage && row.status === "missing" && (
                        <button className="mt-2 text-xs px-3 py-1 rounded-lg bg-amber-300 text-slate-950 font-bold" onClick={() => createReminder(row)}>
                          Queue reminder
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="rounded-3xl bg-white/10 border border-white/10 p-5">
              <h2 className="text-xl font-black">Next automation hooks</h2>
              <ul className="mt-3 space-y-2 text-sm text-slate-300">
                <li>Automated review reminders and manager escalations</li>
                <li>AI document analysis and question generation</li>
                <li>Minimum viewing time and page-by-page confirmation</li>
                <li>Automatic document translation by user language</li>
              </ul>
            </div>
          </aside>
        </section>
      </div>

      {selectedPolicy && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-white text-slate-950 rounded-3xl max-w-3xl w-full p-6 space-y-4 max-h-[92vh] overflow-y-auto">
            <div className="flex justify-between gap-4">
              <div>
                <h2 className="text-2xl font-black">Confirm acknowledgement</h2>
                <p className="text-slate-600">{selectedPolicy.title} • version {selectedPolicy.currentVersion || 1}</p>
              </div>
              <button onClick={() => { setSelectedPolicy(null); setQuizAnswers({}); setQuizResult(null); }} className="text-slate-500 font-bold">Close</button>
            </div>
            <div className="rounded-2xl bg-slate-100 p-4 text-sm text-slate-700">
              I confirm that I have opened, read, and understood this policy/procedure. My signature and timestamp will be stored as a compliance record.
            </div>
            {selectedPolicy.assessment?.enabled && (selectedPolicy.assessment?.questions || []).length > 0 && (
              <div className="rounded-2xl border border-slate-200 p-4 space-y-4">
                <div>
                  <h3 className="font-black">Closeout assessment</h3>
                  <p className="text-sm text-slate-600">
                    Pass mark: {selectedPolicy.assessment?.passThreshold || 80}%{selectedPolicy.assessment?.requireBeforeSignoff ? " • Must pass before sign-off" : ""}
                  </p>
                </div>
                {(selectedPolicy.assessment.questions || []).map((question, index) => (
                  <div key={question.id} className="rounded-xl bg-slate-50 border border-slate-200 p-3">
                    <div className="font-bold mb-2">{index + 1}. {question.question}</div>
                    {question.type === "multiple_choice" && (
                      <div className="grid sm:grid-cols-2 gap-2">
                        {(question.options || []).map((option) => (
                          <label key={option} className="flex items-center gap-2 text-sm">
                            <input type="radio" name={question.id} checked={quizAnswers[question.id] === option} onChange={() => setQuizAnswers({ ...quizAnswers, [question.id]: option })} />
                            {option}
                          </label>
                        ))}
                      </div>
                    )}
                    {question.type === "true_false" && (
                      <div className="flex gap-4">
                        {["true", "false"].map((option) => (
                          <label key={option} className="flex items-center gap-2 text-sm">
                            <input type="radio" name={question.id} checked={quizAnswers[question.id] === option} onChange={() => setQuizAnswers({ ...quizAnswers, [question.id]: option })} />
                            {option === "true" ? "True" : "False"}
                          </label>
                        ))}
                      </div>
                    )}
                    {question.type === "short_answer" && (
                      <textarea className="w-full rounded-xl border border-slate-200 p-3" value={quizAnswers[question.id] || ""} onChange={(e) => setQuizAnswers({ ...quizAnswers, [question.id]: e.target.value })} placeholder="Type your answer" />
                    )}
                  </div>
                ))}
                {quizResult && (
                  <div className={`rounded-xl p-3 text-sm ${quizResult.passed ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-900"}`}>
                    Score: {quizResult.score ?? 0}% • {quizResult.passed ? "Passed" : "Not passed yet"}
                  </div>
                )}
              </div>
            )}
            <SignatureCanvas ref={signatureRef} canvasProps={{ className: "w-full h-48 border rounded-2xl bg-white" }} />
            <div className="flex justify-between">
              <button className="px-4 py-2 rounded-xl bg-slate-200 font-bold" onClick={() => signatureRef.current?.clear()}>Clear</button>
              <button disabled={saving} className="px-5 py-2 rounded-xl bg-emerald-500 font-black text-white" onClick={handleSignPolicy}>
                {saving ? "Signing..." : "Sign acknowledgement"}
              </button>
            </div>
          </div>
        </div>
      )}

      {versionTarget && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-white text-slate-950 rounded-3xl max-w-xl w-full p-6 space-y-4">
            <div className="flex justify-between gap-4">
              <div>
                <h2 className="text-2xl font-black">Upload new version</h2>
                <p className="text-slate-600">{versionTarget.title} • next version {(versionTarget.currentVersion || 1) + 1}</p>
              </div>
              <button onClick={() => setVersionTarget(null)} className="text-slate-500 font-bold">Close</button>
            </div>
            <input type="file" onChange={(e) => setNewVersionFile(e.target.files?.[0] || null)} />
            <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
              Uploading a new version will require assigned users to sign off again for the latest version.
            </div>
            <button disabled={saving || !newVersionFile} className="w-full px-5 py-3 rounded-xl bg-slate-950 text-white font-black disabled:opacity-50" onClick={handleUploadNewVersion}>
              {saving ? "Uploading..." : "Publish new version"}
            </button>
          </div>
        </div>
      )}

      {assessmentTarget && assessmentDraft && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-white text-slate-950 rounded-3xl max-w-4xl w-full p-6 space-y-4 max-h-[92vh] overflow-y-auto">
            <div className="flex justify-between gap-4">
              <div>
                <h2 className="text-2xl font-black">Assessment builder</h2>
                <p className="text-slate-600">{assessmentTarget.title}</p>
              </div>
              <button onClick={() => { setAssessmentTarget(null); setAssessmentDraft(null); }} className="text-slate-500 font-bold">Close</button>
            </div>

            <div className="grid md:grid-cols-4 gap-3 rounded-2xl bg-slate-100 p-4">
              <label className="flex items-center gap-2 text-sm font-bold">
                <input type="checkbox" checked={assessmentDraft.enabled} onChange={(e) => setAssessmentDraft({ ...assessmentDraft, enabled: e.target.checked })} />
                Enabled
              </label>
              <label className="text-sm">
                <span className="block text-slate-600 mb-1">Pass threshold</span>
                <input className="w-full rounded-xl border border-slate-300 p-2" type="number" value={assessmentDraft.passThreshold} onChange={(e) => setAssessmentDraft({ ...assessmentDraft, passThreshold: e.target.value })} />
              </label>
              <label className="flex items-center gap-2 text-sm font-bold md:col-span-2">
                <input type="checkbox" checked={assessmentDraft.requireBeforeSignoff} onChange={(e) => setAssessmentDraft({ ...assessmentDraft, requireBeforeSignoff: e.target.checked })} />
                Require pass before sign-off is accepted
              </label>
            </div>

            <div className="rounded-2xl border border-indigo-100 bg-indigo-50 p-4 space-y-3">
              <div>
                <h3 className="font-black text-indigo-950">AI policy summary & question generation</h3>
                <p className="text-sm text-indigo-900">
                  Generate a reusable summary first, then create assessment questions from that summary. For large PDFs, paste the key sections below if the full PDF is too large.
                </p>
              </div>
              <textarea
                className="w-full min-h-28 rounded-xl border border-indigo-200 p-3 text-sm"
                value={assessmentDraft.aiSourceText || ""}
                onChange={(e) => setAssessmentDraft({ ...assessmentDraft, aiSourceText: e.target.value })}
                placeholder="Optional: paste the relevant policy wording, clauses, or a short summary..."
              />
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  disabled={aiSummarising}
                  className="px-4 py-2 rounded-xl bg-indigo-600 text-white font-bold disabled:opacity-50"
                  onClick={generatePolicySummary}
                >
                  {aiSummarising ? "Generating summary..." : "Generate policy summary"}
                </button>
                {assessmentDraft.aiSummaryModel && (
                  <span className="self-center text-xs text-indigo-900">
                    Summary generated with {assessmentDraft.aiSummaryModel}{assessmentDraft.aiSummaryUsedPolicyFile ? " using the uploaded PDF" : ""}.
                  </span>
                )}
              </div>
              <label className="block">
                <span className="block text-xs uppercase tracking-[0.16em] text-indigo-900 mb-2">Saved policy summary used for questions</span>
                <textarea
                  className="w-full min-h-40 rounded-xl border border-indigo-200 p-3 text-sm"
                  value={assessmentDraft.aiDocumentSummary || ""}
                  onChange={(e) => setAssessmentDraft({ ...assessmentDraft, aiDocumentSummary: e.target.value })}
                  placeholder="Generate or paste a reusable policy summary here. Questions will be generated from this before trying the full PDF."
                />
              </label>
              {assessmentDraft.aiModel && (
                <p className="text-xs text-indigo-900">
                  Last questions generated with {assessmentDraft.aiModel}{assessmentDraft.aiUsedStoredSummary ? " using the saved summary" : assessmentDraft.aiUsedPolicyFile ? " using the uploaded PDF" : ""}. Review all generated questions before saving.
                </p>
              )}
            </div>

            <div className="space-y-4">
              {(assessmentDraft.questions || []).map((question, index) => (
                <div key={question.id} className="rounded-2xl border border-slate-200 p-4 space-y-3">
                  <div className="flex justify-between gap-3">
                    <strong>Question {index + 1}</strong>
                    <button
                      className="text-red-600 font-bold text-sm"
                      onClick={() => setAssessmentDraft({ ...assessmentDraft, questions: assessmentDraft.questions.filter((item) => item.id !== question.id) })}
                    >
                      Remove
                    </button>
                  </div>
                  <div className="grid md:grid-cols-4 gap-3">
                    <select className="rounded-xl border border-slate-300 p-2" value={question.type} onChange={(e) => updateAssessmentQuestion(question.id, { type: e.target.value, correctAnswer: "", options: e.target.value === "true_false" ? ["true", "false"] : question.options || ["", "", "", ""] })}>
                      <option value="multiple_choice">Multiple choice</option>
                      <option value="true_false">True / False</option>
                      <option value="short_answer">Short answer</option>
                    </select>
                    <input className="rounded-xl border border-slate-300 p-2 md:col-span-3" value={question.question} onChange={(e) => updateAssessmentQuestion(question.id, { question: e.target.value })} placeholder="Question text" />
                  </div>
                  {question.type === "multiple_choice" && (
                    <div className="grid md:grid-cols-2 gap-2">
                      {(question.options || ["", "", "", ""]).map((option, optionIndex) => (
                        <input key={optionIndex} className="rounded-xl border border-slate-300 p-2" value={option} onChange={(e) => updateQuestionOption(question.id, optionIndex, e.target.value)} placeholder={`Option ${optionIndex + 1}`} />
                      ))}
                    </div>
                  )}
                  {question.type !== "short_answer" && (
                    <label className="block text-sm">
                      <span className="block text-slate-600 mb-1">Correct answer</span>
                      {question.type === "true_false" ? (
                        <select className="rounded-xl border border-slate-300 p-2" value={question.correctAnswer} onChange={(e) => updateAssessmentQuestion(question.id, { correctAnswer: e.target.value })}>
                          <option value="">Select</option>
                          <option value="true">True</option>
                          <option value="false">False</option>
                        </select>
                      ) : (
                        <input className="w-full rounded-xl border border-slate-300 p-2" value={question.correctAnswer} onChange={(e) => updateAssessmentQuestion(question.id, { correctAnswer: e.target.value })} placeholder="Must match one option" />
                      )}
                    </label>
                  )}
                  {question.type === "short_answer" && (
                    <div className="rounded-xl bg-blue-50 border border-blue-100 p-3 text-sm text-blue-900">
                      Short answers are stored for audit/manual review and count as complete when the user enters text.
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="flex flex-wrap justify-between gap-3">
              <button className="px-4 py-2 rounded-xl bg-slate-200 font-bold" onClick={() => setAssessmentDraft({ ...assessmentDraft, questions: [...(assessmentDraft.questions || []), createBlankQuestion()] })}>
                Add question
              </button>
              <div className="flex gap-3">
                <button disabled={aiGenerating} className="px-4 py-2 rounded-xl bg-indigo-100 text-indigo-800 font-bold disabled:opacity-50" onClick={generateAssessmentWithAI}>
                  {aiGenerating ? "Generating..." : "Generate with AI"}
                </button>
                <button disabled={saving} className="px-5 py-2 rounded-xl bg-slate-950 text-white font-black disabled:opacity-50" onClick={saveAssessment}>
                  {saving ? "Saving..." : "Save assessment"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
